/**
 * "Disbursed Loan Details" — the standalone Disbursed Loans screen.
 *
 * WHAT IT SHOWS
 * -------------
 * KPI cards + a cumulative trend chart above the full disbursal register.
 * Every TLoanBaseDisbursed row, with `payment_start_date` and, resolved from
 * the linked LOS application, the customer's name and business name. Server
 * side this is DisbursedLoansListView (backend/lms/views_staff_ops.py), with
 * DisbursedLoansMetricsView and DisbursedLoansExportView behind the same
 * `disbursed_loans` permission.
 *
 * SIMULATION LOANS ARE EXCLUDED (plan decision G)
 * ----------------------------------------------
 * The register now hides loans whose `intellend_loan_id` starts with `SIM_`,
 * via the same `_non_simulated_loans_qs()` helper every other staff-ops
 * dashboard uses. On any environment that has run the simulator the row count
 * DROPS the moment this ships, which is why the subtitle says so on screen — an
 * unexplained drop in a count the business watches is indistinguishable from a
 * data-loss incident.
 *
 * WHY IT IS NOT A TAB IN LmsStaffOpsHub
 * ------------------------------------
 * It is reached from its own sidebar entry ("LMS -> Disbursed Loans", menu
 * permission id `lms-disbursed-loans`) and is rendered directly by
 * AdminDashboard, so it has no entry in that hub's TAB_DEFS. The API is still
 * gated by the `disbursed_loans` LmsStaffPermission tab, which is the real
 * security boundary — the sidebar permission only controls whether the link is
 * visible. That is why a 403 is handled explicitly below: a role can be given
 * the menu entry without the API permission, and the screen has to explain
 * that rather than look broken.
 *
 * PAGING / SEARCH / FILTERS
 * -------------------------
 * All SERVER side, matching LoanProfilePage: `paginationServer` with
 * `paginationTotalRows` from the DRF envelope's `count`, a debounced `search`
 * param, and an inclusive `date_from`/`date_to` disbursal-date range that the
 * CSV export honours identically (they share one backend queryset helper, so the
 * file can never disagree with the screen). Sorting is client-side within the
 * loaded page only (react-data-table-component's default) — the same limitation
 * the other staff-ops tables have, and the reason the backend applies a
 * deterministic `-date_of_disbursal, intellend_loan_id` order so paging cannot
 * repeat or skip rows.
 *
 * THE CARDS AND CHART DO NOT FOLLOW THESE FILTERS (plan decision F). They are
 * always global. A "Total Disbursed" card that quietly followed a search box
 * would still be read as a portfolio total.
 *
 * URL STATE
 * ---------
 * `search`/`page`/`page_size`/`date_from`/`date_to` are mirrored into query
 * params, so a filtered view is itself linkable — the entire point of the
 * dedicated `/admin-dashboard/lms/disbursed-loans` route.
 */
import React from "react";
import DataTable from "react-data-table-component";
import { isAxiosError } from "axios";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    Search,
    RefreshCcw,
    ShieldAlert,
    AlertTriangle,
    Download,
    Loader2,
    FilterX,
} from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

import lmsStaffService, {
    DisbursedLoanRow,
    ApprovedPipelineUser,
    DisbursedLoansFilterOptions,
    getLmsApiErrorMessage,
    getLmsApiErrorStatus,
} from "./lmsStaffApi";
import DisbursedLoansKpiCards from "./DisbursedLoansKpiCards";
import DisbursedLoansTrendChart from "./DisbursedLoansTrendChart";
import {
    formatAmount,
    formatDate,
    formatDateTimeIST,
    dataTableStyles as customStyles,
} from "./formatters";

/** Matches the debounce used by the other staff-ops search boxes. */
const DEBOUNCE_MS = 350;

/** Rendered wherever the LOS mapping could not supply a value. */
const NOT_AVAILABLE = "—";

const DEFAULT_PAGE_SIZE = 25;

/**
 * Metrics query key factory. Includes `referenceMonth` so that selecting a
 * different month triggers a fresh fetch and each month is cached separately.
 * `"current"` is the sentinel for live MTD (no `reference_month` param).
 */
const metricsQueryKey = (referenceMonth: string) =>
    ["lms-disbursed-loans-metrics", referenceMonth || "current"] as const;
const PIPELINE_QUERY_KEY = ["lms-approved-pipeline"] as const;
const FILTER_OPTIONS_QUERY_KEY = ["lms-disbursed-loans-filter-options"] as const;

/**
 * The IST-stamped filename the backend puts in `Content-Disposition`.
 *
 * Preferred over inventing a client-side name because the server's timestamp is
 * the moment the data was actually read, in the timezone every figure on this
 * screen is computed in. Falls back to a local name only if the header is
 * missing or unreadable (a proxy that strips it, or a CORS setup that does not
 * expose it).
 */
const filenameFromDisposition = (disposition?: string): string | null => {
    if (!disposition) return null;
    // Handles both `filename="x.csv"` and RFC 5987 `filename*=UTF-8''x.csv`.
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
    if (utf8?.[1]) {
        try {
            return decodeURIComponent(utf8[1].trim().replace(/^"|"$/g, ""));
        } catch {
            /* fall through to the plain form */
        }
    }
    const plain = /filename="?([^";]+)"?/i.exec(disposition);
    return plain?.[1]?.trim() || null;
};

/**
 * A 400 from the export endpoint arrives as a **Blob** (because the request
 * asked for one), so the `{"error": "..."}` body has to be read back out of it
 * before it can be shown to the user. Without this the toast would say
 * "[object Blob]" for exactly the case the backend went to the trouble of
 * explaining — a malformed or inverted date range.
 */
const readBlobError = async (data: unknown): Promise<string | null> => {
    if (!(data instanceof Blob)) return null;
    try {
        const text = await data.text();
        const parsed = JSON.parse(text);
        return parsed?.error || parsed?.detail || null;
    } catch {
        return null;
    }
};

const DisbursedLoansTable: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    /** `"YYYY-MM"` when a past month is selected; `""` for live MTD. */
    const [selectedMonth, setSelectedMonth] = React.useState("");

    // Initialised FROM the URL so a shared link opens on the same filtered view
    // it was copied from. Read once, on mount: after that the state is the
    // source of truth and the URL follows it.
    const initial = React.useRef({
        search: searchParams.get("search") ?? "",
        page: Number(searchParams.get("page")) || 1,
        pageSize: Number(searchParams.get("page_size")) || DEFAULT_PAGE_SIZE,
        dateFrom: searchParams.get("date_from") ?? "",
        dateTo: searchParams.get("date_to") ?? "",
        lenderCode: searchParams.get("lender_code") ?? "",
        bapName: searchParams.get("bap_name") ?? "",
        loanAmountBucket: searchParams.get("loan_amount_bucket") ?? "",
    });

    const [searchInput, setSearchInput] = React.useState(initial.current.search);
    const [search, setSearch] = React.useState(initial.current.search);
    const [dateFrom, setDateFrom] = React.useState(initial.current.dateFrom);
    const [dateTo, setDateTo] = React.useState(initial.current.dateTo);
    const [lenderCode, setLenderCode] = React.useState(initial.current.lenderCode);
    const [bapName, setBapName] = React.useState(initial.current.bapName);
    const [loanAmountBucket, setLoanAmountBucket] = React.useState(initial.current.loanAmountBucket);
    const [page, setPage] = React.useState(initial.current.page);
    const [pageSize, setPageSize] = React.useState(initial.current.pageSize);
    const [isExporting, setIsExporting] = React.useState(false);

    // Debounced: the backend does the filtering, so this avoids a request per
    // keystroke.
    React.useEffect(() => {
        const handle = setTimeout(() => {
            setSearch(searchInput.trim());
        }, DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [searchInput]);

    /**
     * Reset to page 1 whenever a filter changes. Without this, narrowing a filter
     * while on page 7 leaves the request asking for a page that no longer exists
     * and the table renders EMPTY — which reads as "no matching loans" rather
     * than "you are past the end", the single most misleading failure this screen
     * can produce.
     *
     * `isFirstRender` guards the mount pass so a page number restored from a
     * shared URL is not immediately clobbered back to 1.
     */
    const isFirstRender = React.useRef(true);
    // Flipped on every filter change and handed to `paginationResetDefaultPage`.
    // Setting `page` back to 1 only changes what we REQUEST; DataTable keeps its
    // own internal page number, so without this the footer would keep reading
    // "page 7" over page-1 data. `paginationResetDefaultPage` reacts to the value
    // CHANGING, which is why this is a toggle rather than a boolean flag.
    const [paginationResetToggle, setPaginationResetToggle] = React.useState(false);
    React.useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setPage(1);
        setPaginationResetToggle((prev) => !prev);
    }, [search, dateFrom, dateTo, lenderCode, bapName, loanAmountBucket]);

    // State -> URL. `replace` so typing in the search box does not push a
    // history entry per keystroke and trap the user behind dozens of Backs.
    React.useEffect(() => {
        const next = new URLSearchParams(searchParams);
        const sync = (key: string, value: string) => {
            if (value) next.set(key, value);
            else next.delete(key);
        };
        sync("search", search);
        sync("date_from", dateFrom);
        sync("date_to", dateTo);
        sync("lender_code", lenderCode);
        sync("bap_name", bapName);
        sync("loan_amount_bucket", loanAmountBucket);
        sync("page", page > 1 ? String(page) : "");
        sync("page_size", pageSize !== DEFAULT_PAGE_SIZE ? String(pageSize) : "");

        if (next.toString() !== searchParams.toString()) {
            setSearchParams(next, { replace: true });
        }
        // `searchParams`/`setSearchParams` are intentionally omitted: including
        // them re-runs this effect with the value it just wrote.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, dateFrom, dateTo, lenderCode, bapName, loanAmountBucket, page, pageSize]);

    const hasFilters = Boolean(searchInput || search || dateFrom || dateTo || lenderCode || bapName || loanAmountBucket);

    const clearFilters = () => {
        setSearchInput("");
        setSearch("");
        setDateFrom("");
        setDateTo("");
        setLenderCode("");
        setBapName("");
        setLoanAmountBucket("");
        setPage(1);
    };

    const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
        queryKey: [
            "lms-staff-disbursed-loans",
            search,
            dateFrom,
            dateTo,
            lenderCode,
            bapName,
            loanAmountBucket,
            page,
            pageSize,
        ],
        queryFn: async () => {
            const response = await lmsStaffService.getDisbursedLoans({
                search: search || undefined,
                date_from: dateFrom || undefined,
                date_to: dateTo || undefined,
                lender_code: lenderCode || undefined,
                bap_name: bapName || undefined,
                loan_amount_bucket: loanAmountBucket || undefined,
                page,
                page_size: pageSize,
            });
            return response.data;
        },
    });

    /**
     * The ONE metrics query for both the cards and the chart.
     * The query key includes `selectedMonth` so that changing the month
     * triggers a fresh fetch and each month is cached separately.
     * When `selectedMonth` is empty the endpoint is called without params
     * (live MTD — plan decision F behaviour preserved).
     */
    const metricsQuery = useQuery({
        queryKey: metricsQueryKey(selectedMonth),
        queryFn: async () => {
            const response = await lmsStaffService.getDisbursedLoanMetrics(
                selectedMonth || undefined
            );
            return response.data;
        },
        // A 403 here is not retryable: the user simply lacks the permission, and
        // hammering the endpoint would only fill the logs.
        retry: false,
    });

    const pipelineQuery = useQuery({
        queryKey: PIPELINE_QUERY_KEY,
        queryFn: async () => {
            const response = await lmsStaffService.getApprovedPipeline();
            return response.data;
        },
        retry: false,
    });

    const filterOptionsQuery = useQuery({
        queryKey: FILTER_OPTIONS_QUERY_KEY,
        queryFn: async () => {
            const response = await lmsStaffService.getDisbursedLoansFilterOptions();
            return response.data as DisbursedLoansFilterOptions;
        },
        // Filter options are stable — only refresh when the user explicitly
        // refreshes the page. Stale options are far less harmful than an extra
        // request on every render.
        staleTime: 5 * 60 * 1000,
        retry: false,
    });

    const rows: DisbursedLoanRow[] = data?.results ?? [];
    const status = getLmsApiErrorStatus(error);
    const totalCount = data?.count ?? 0;

    /**
     * The 400 the backend returns for a malformed date or an inverted range,
     * surfaced INLINE rather than as an empty table. "No disbursed loans yet"
     * for a typo'd date would be a lie about the data.
     */
    const filterError: string | null =
        isError && status === 400
            ? getLmsApiErrorMessage(error, "Invalid date filter.")
            : null;

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const response = await lmsStaffService.exportDisbursedLoans({
                search: search || undefined,
                date_from: dateFrom || undefined,
                date_to: dateTo || undefined,
                lender_code: lenderCode || undefined,
                bap_name: bapName || undefined,
                loan_amount_bucket: loanAmountBucket || undefined,
            });

            // The backend already prepends the UTF-8 BOM, so the blob is wrapped
            // with the server's own content type and NOT re-encoded here —
            // touching the bytes is what would corrupt the BOM Excel needs.
            const blob = new Blob([response.data], {
                type:
                    response.headers?.["content-type"] ||
                    "text/csv;charset=utf-8",
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                filenameFromDisposition(
                    response.headers?.["content-disposition"]
                ) || `disbursed_loans_${new Date().toISOString().slice(0, 10)}.csv`
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            // Revoked immediately: the download has already been handed to the
            // browser, and leaving it alive leaks the whole file in memory for
            // the lifetime of the tab.
            URL.revokeObjectURL(url);

            toast.success("Export started", {
                description: `${totalCount.toLocaleString("en-IN")} ${
                    totalCount === 1 ? "loan" : "loans"
                } exported${hasFilters ? " with the current filters" : ""}.`,
            });
        } catch (err: unknown) {
            const blobMessage = await readBlobError(
                isAxiosError(err) ? err.response?.data : undefined
            );
            toast.error("Export failed", {
                description:
                    blobMessage ||
                    getLmsApiErrorMessage(
                        err,
                        "Could not generate the CSV. Please try again."
                    ),
            });
        } finally {
            setIsExporting(false);
        }
    };

    const columns = React.useMemo(
        () => [
            {
                name: "Loan ID",
                selector: (row: DisbursedLoanRow) => row.intellend_loan_id,
                sortable: true,
                width: "170px",
                cell: (row: DisbursedLoanRow) => (
                    <span className="font-mono text-xs">{row.intellend_loan_id}</span>
                ),
            },
            {
                name: "Customer Name",
                // `selector` drives sorting, so it must return a comparable
                // primitive; `cell` owns the presentation. Sorting on the
                // placeholder would group all unmapped loans together, which is
                // the desired behaviour here.
                selector: (row: DisbursedLoanRow) => row.customer_name || "",
                sortable: true,
                minWidth: "170px",
                cell: (row: DisbursedLoanRow) =>
                    row.customer_name ? (
                        <span className="text-sm">{row.customer_name}</span>
                    ) : (
                        <span className="text-xs text-gray-400">{NOT_AVAILABLE}</span>
                    ),
            },
            {
                name: "Business Name",
                selector: (row: DisbursedLoanRow) => row.business_name || "",
                sortable: true,
                minWidth: "190px",
                cell: (row: DisbursedLoanRow) =>
                    row.business_name ? (
                        <span className="text-sm">{row.business_name}</span>
                    ) : (
                        <span className="text-xs text-gray-400">{NOT_AVAILABLE}</span>
                    ),
            },
            {
                name: "Phone Number",
                selector: (row: DisbursedLoanRow) => row.phone_number || "",
                sortable: true,
                width: "150px",
                cell: (row: DisbursedLoanRow) =>
                    row.phone_number ? (
                        <span className="font-mono text-xs">{row.phone_number}</span>
                    ) : (
                        <span className="text-xs text-gray-400">{NOT_AVAILABLE}</span>
                    ),
            },
            {
                name: "Payment Start Date",
                selector: (row: DisbursedLoanRow) => row.payment_start_date || "",
                sortable: true,
                width: "160px",
                cell: (row: DisbursedLoanRow) => formatDate(row.payment_start_date),
            },
            {
                name: "Approved Loan Amt",
                selector: (row: DisbursedLoanRow) =>
                    row.approved_loan_amount === null ? "" : String(row.approved_loan_amount),
                sortable: true,
                width: "150px",
                cell: (row: DisbursedLoanRow) => formatAmount(row.approved_loan_amount),
            },
            {
                name: "Loan Amount",
                selector: (row: DisbursedLoanRow) =>
                    row.loan_amount === null ? "" : String(row.loan_amount),
                sortable: true,
                width: "150px",
                cell: (row: DisbursedLoanRow) => formatAmount(row.loan_amount),
            },
            {
                name: "Disbursed Amount",
                selector: (row: DisbursedLoanRow) =>
                    row.disbursed_amount === null ? "" : String(row.disbursed_amount),
                sortable: true,
                width: "160px",
                cell: (row: DisbursedLoanRow) => formatAmount(row.disbursed_amount),
            },
            {
                name: "Amt in Bank",
                selector: (row: DisbursedLoanRow) =>
                    row.offer_loan_amt_net === null ? "" : String(row.offer_loan_amt_net),
                sortable: true,
                width: "140px",
                cell: (row: DisbursedLoanRow) => formatAmount(row.offer_loan_amt_net),
            },
            {
                name: "Daily EDI",
                selector: (row: DisbursedLoanRow) =>
                    row.offer_daily_repayment === null ? "" : String(row.offer_daily_repayment),
                sortable: true,
                width: "130px",
                cell: (row: DisbursedLoanRow) => formatAmount(row.offer_daily_repayment),
            },
            {
                name: "Total Fee",
                selector: (row: DisbursedLoanRow) =>
                    row.offer_total_fee === null ? "" : String(row.offer_total_fee),
                sortable: true,
                width: "130px",
                cell: (row: DisbursedLoanRow) => formatAmount(row.offer_total_fee),
            },
            {
                name: "Repay Total",
                selector: (row: DisbursedLoanRow) =>
                    row.offer_repay_total === null ? "" : String(row.offer_repay_total),
                sortable: true,
                width: "140px",
                cell: (row: DisbursedLoanRow) => formatAmount(row.offer_repay_total),
            },
            {
                name: "ROI",
                selector: (row: DisbursedLoanRow) =>
                    row.roi === null ? "" : String(row.roi),
                sortable: true,
                width: "90px",
                cell: (row: DisbursedLoanRow) =>
                    row.roi === null || row.roi === undefined
                        ? NOT_AVAILABLE
                        : `${Number(row.roi)}%`,
            },
            {
                name: "Tenure",
                selector: (row: DisbursedLoanRow) => row.tenure ?? 0,
                sortable: true,
                width: "100px",
                cell: (row: DisbursedLoanRow) => row.tenure ?? NOT_AVAILABLE,
            },
            {
                name: "Disbursal Date",
                    selector: (row: DisbursedLoanRow) => row.date_of_disbursal || "",
                    sortable: true,
                    width: "180px",
                    cell: (row: DisbursedLoanRow) => formatDateTimeIST(row.date_of_disbursal),
            },
            {
                name: "Frequency",
                selector: (row: DisbursedLoanRow) => row.payment_freq || "",
                sortable: true,
                width: "120px",
                cell: (row: DisbursedLoanRow) =>
                    row.payment_freq ? (
                        <Badge variant="outline" className="text-xs">
                            {row.payment_freq}
                        </Badge>
                    ) : (
                        <span className="text-xs text-gray-400">{NOT_AVAILABLE}</span>
                    ),
            },
            {
                name: "Stage",
                selector: (row: DisbursedLoanRow) => row.stage_display_name || "",
                sortable: true,
                minWidth: "140px",
                cell: (row: DisbursedLoanRow) =>
                    row.stage_display_name ? (
                        <Badge variant="outline" className="text-xs">
                            {row.stage_display_name}
                        </Badge>
                    ) : (
                        <span className="text-xs text-gray-400">{NOT_AVAILABLE}</span>
                    ),
            },
            {
                name: "Lender",
                selector: (row: DisbursedLoanRow) => row.lender_code || "",
                sortable: true,
                width: "120px",
                cell: (row: DisbursedLoanRow) => row.lender_code || NOT_AVAILABLE,
            },
        ],
        []
    );

    // 403 — the role has the sidebar entry but not the `disbursed_loans` tab
    // permission. Distinguished from a generic failure because the remedy is
    // completely different (ask a superuser to grant it, not retry).
    if (isError && status === 403) {
        return (
            <div className="w-full rounded-xl border border-amber-200 bg-amber-50 p-8 text-center">
                <ShieldAlert className="mx-auto h-10 w-10 text-amber-500" />
                <p className="mt-3 text-sm font-medium text-amber-800">
                    You do not have permission to view Disbursed Loan Details.
                </p>
                <p className="mt-1 text-xs text-amber-700">
                    Ask a superuser to grant you view access to the
                    &ldquo;Disbursed Loan Details&rdquo; tab under LMS Permissions.
                </p>
            </div>
        );
    }

    // A 400 is the user's own date filter, not an outage: keep the screen (and
    // the filter inputs) mounted so the mistake can be corrected in place. Only
    // genuinely unexpected failures replace the whole view.
    if (isError && status !== 400) {
        return (
            <div className="w-full rounded-xl border border-red-100 bg-red-50 p-8 text-center">
                <AlertTriangle className="mx-auto h-10 w-10 text-red-400" />
                <p className="mt-3 text-sm font-medium text-red-700">
                    {getLmsApiErrorMessage(error, "Unable to load disbursed loans.")}
                </p>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => refetch()}
                    className="mt-4"
                >
                    <RefreshCcw className="mr-2 h-4 w-4" /> Try again
                </Button>
            </div>
        );
    }

    return (
        <div className="w-full space-y-4">
            {/* Month-picker: placed above the KPI cards so it is the first
                control the user sees. Selecting a past month re-fetches the
                metrics for that full month; clearing it returns to live MTD.
                `max` is the current month so future months cannot be selected. */}
            <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-xs font-medium text-gray-600">
                    <span>📅 View Month:</span>
                    <input
                        type="month"
                        value={selectedMonth}
                        max={new Date().toISOString().slice(0, 7)}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        className="h-9 rounded-md border border-gray-200 bg-white px-3 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-intellend-green/30"
                        title="Select a past month to view its full-month metrics. Clear to return to live MTD."
                    />
                </label>
                {selectedMonth && (
                    <button
                        type="button"
                        onClick={() => setSelectedMonth("")}
                        className="text-xs text-gray-400 underline hover:text-gray-600"
                    >
                        Reset to live MTD
                    </button>
                )}
            </div>

            {/* Cards and chart share ONE metrics query (metricsQueryKey), so the
                endpoint is fetched once even though two components consume it.
                Both are GLOBAL and deliberately ignore the table filters below. */}
            <DisbursedLoansKpiCards
                metrics={metricsQuery.data}
                isLoading={metricsQuery.isLoading}
                isError={metricsQuery.isError}
                referenceMonth={selectedMonth || undefined}
            />
            <DisbursedLoansTrendChart
                metrics={metricsQuery.data}
                isLoading={metricsQuery.isLoading}
                isError={metricsQuery.isError}
                referenceMonth={selectedMonth || undefined}
            />

            {/* ── Approved Pipeline section ─────────────────────────────────
                Users whose Caprion final status is APPROVED but who have not
                yet been disbursed. Shown between the chart and the register
                so ops staff can see what is coming before it hits the table.
                Always rendered (even when count == 0) so ops staff know the
                section exists and the empty state is intentional. */}
            {!pipelineQuery.isLoading && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 shadow-sm">
                    <div className="mb-3 flex items-center justify-between">
                        <div>
                            <h2 className="text-base font-semibold text-amber-800">
                                ⏳ Approved — Pending Disbursal
                                <span className="ml-2 rounded-full bg-amber-200 px-2 py-0.5 text-xs font-medium text-amber-900">
                                    {pipelineQuery.data?.count ?? 0}
                                </span>
                            </h2>
                            <p className="mt-0.5 text-xs text-amber-700">
                                Approved by lender but not yet disbursed.
                                {pipelineQuery.data?.total_approved_amount && (
                                    <> Total approved: <span className="font-medium">
                                        {formatAmount(pipelineQuery.data.total_approved_amount)}
                                    </span></>
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="overflow-x-auto rounded-lg border border-amber-200">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-amber-100 text-amber-800">
                                    <th className="px-3 py-2 text-left font-medium">Name</th>
                                    <th className="px-3 py-2 text-left font-medium">Phone</th>
                                    <th className="px-3 py-2 text-right font-medium">Approved Amt</th>
                                    <th className="px-3 py-2 text-right font-medium">Loan Amt</th>
                                    <th className="px-3 py-2 text-left font-medium">Current Stage</th>
                                    <th className="px-3 py-2 text-left font-medium">Last Updated</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(pipelineQuery.data?.results ?? []).length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="text-center py-4 text-gray-400 italic">
                                            No pending disbursals
                                        </td>
                                    </tr>
                                ) : (
                                    (pipelineQuery.data?.results ?? []).map((u: ApprovedPipelineUser, idx: number) => (
                                        <tr
                                            key={u.user_id}
                                            className={idx % 2 === 0 ? "bg-white" : "bg-amber-50/60"}
                                        >
                                            <td className="px-3 py-2 font-medium text-gray-800">
                                                {u.name || <span className="text-gray-400">—</span>}
                                            </td>
                                            <td className="px-3 py-2 font-mono text-gray-600">
                                                {u.phone || <span className="text-gray-400">—</span>}
                                            </td>
                                            <td className="px-3 py-2 text-right font-semibold text-orange-600">
                                                {formatAmount(u.approved_loan_amount)}
                                            </td>
                                            <td className="px-3 py-2 text-right text-gray-700">
                                                {formatAmount(u.loan_amount)}
                                            </td>
                                            <td className="px-3 py-2 text-gray-600">
                                                {u.stage_name || <span className="text-gray-400">—</span>}
                                            </td>
                                            <td className="px-3 py-2 text-gray-500">
                                                {u.last_updated
                                                    ? u.last_updated.slice(0, 10)
                                                    : <span className="text-gray-400">—</span>}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="mb-6 flex flex-col items-start justify-between gap-4 xl:flex-row">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">
                            Disbursed Loan Details
                        </h1>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500">
                            All disbursed loans, including closed ones, with the
                            customer and business behind each loan.{" "}
                            {/* The row count drops when simulation loans are hidden;
                                saying so on screen is what stops that looking like
                                missing data. */}
                            <span className="text-gray-400">
                                Simulation loans (IDs starting with{" "}
                                <span className="font-mono">SIM_</span>) are excluded.
                            </span>
                            {typeof data?.count === "number" && (
                                <>
                                    {" "}
                                    <span className="font-medium text-gray-700">
                                        {data.count.toLocaleString("en-IN")}
                                    </span>{" "}
                                    {data.count === 1 ? "loan" : "loans"}
                                    {hasFilters ? " matched" : " total"}.
                                </>
                            )}
                        </p>
                    </div>

                    <div className="flex w-full flex-col gap-2 xl:w-auto xl:items-end">
                        <div className="flex w-full flex-wrap items-center gap-2 xl:justify-end">
                            <div className="relative w-full sm:w-72">
                                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                <Input
                                    placeholder="Search loan ID, phone, customer or business..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    className="pl-9"
                                />
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => refetch()}
                                disabled={isFetching}
                                className="flex items-center gap-2"
                            >
                                <RefreshCcw
                                    className={`h-4 w-4 ${
                                        isFetching ? "animate-spin" : ""
                                    }`}
                                />
                                Refresh
                            </Button>
                            {/*
                              Disabled at a count of 0 — an "export" that produces
                              a header-only file looks like a broken download
                              rather than an empty result set. Also disabled while
                              the count is still unknown (initial load).
                            */}
                            <Button
                                type="button"
                                size="sm"
                                onClick={handleExport}
                                disabled={isExporting || isLoading || totalCount === 0}
                                className="flex items-center gap-2"
                                title={
                                    totalCount === 0
                                        ? "Nothing to export with the current filters"
                                        : "Download every matching row as CSV"
                                }
                            >
                                {isExporting ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Download className="h-4 w-4" />
                                )}
                                {isExporting ? "Preparing..." : "Export CSV"}
                            </Button>
                        </div>

                        {/* Disbursal-date range. Inclusive on both ends, IST, and
                            honoured identically by the CSV export. */}
                        <div className="flex w-full flex-wrap items-center gap-2 xl:justify-end">
                            <label className="flex items-center gap-1.5 text-xs text-gray-500">
                                From
                                <Input
                                    type="date"
                                    value={dateFrom}
                                    max={dateTo || undefined}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                    className="h-9 w-[150px] text-xs"
                                />
                            </label>
                            <label className="flex items-center gap-1.5 text-xs text-gray-500">
                                To
                                <Input
                                    type="date"
                                    value={dateTo}
                                    min={dateFrom || undefined}
                                    onChange={(e) => setDateTo(e.target.value)}
                                    className="h-9 w-[150px] text-xs"
                                />
                            </label>
                        </div>

                        {/* BAP / Lender / Loan Amount bucket dropdowns */}
                        <div className="flex w-full flex-wrap items-center gap-2 xl:justify-end">
                            {/* BAP filter */}
                            <select
                                value={bapName}
                                onChange={(e) => setBapName(e.target.value)}
                                className="h-9 rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                title="Filter by BAP"
                            >
                                <option value="">All BAPs</option>
                                {(filterOptionsQuery.data?.baps ?? []).map((b) => (
                                    <option key={b} value={b}>{b}</option>
                                ))}
                            </select>

                            {/* Lender filter */}
                            <select
                                value={lenderCode}
                                onChange={(e) => setLenderCode(e.target.value)}
                                className="h-9 rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                title="Filter by Lender"
                            >
                                <option value="">All Lenders</option>
                                {(filterOptionsQuery.data?.lenders ?? []).map((l) => (
                                    <option key={l} value={l}>{l}</option>
                                ))}
                            </select>

                            {/* Loan Amount bucket filter */}
                            <select
                                value={loanAmountBucket}
                                onChange={(e) => setLoanAmountBucket(e.target.value)}
                                className="h-9 rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                title="Filter by Loan Amount Range"
                            >
                                <option value="">Loan Amount Selected</option>
                                {(filterOptionsQuery.data?.loan_amount_buckets ?? []).map((b) => (
                                    <option key={b.key} value={b.key}>{b.label}</option>
                                ))}
                            </select>

                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={clearFilters}
                                disabled={!hasFilters}
                                className="flex items-center gap-1.5 text-xs"
                            >
                                <FilterX className="h-3.5 w-3.5" />
                                Clear filters
                            </Button>
                        </div>
                    </div>
                </div>

                {/*
                  The backend's own message for a malformed date or an inverted
                  range, shown in place of an empty table. Silently rendering "no
                  loans" for a bad input is the failure mode this exists to avoid.
                */}
                {filterError && (
                    <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                        <div className="text-xs text-red-700">
                            <p className="font-medium">{filterError}</p>
                            <p className="mt-0.5 text-red-600">
                                Adjust the date range above; the table is not filtered
                                until it is valid.
                            </p>
                        </div>
                    </div>
                )}

                <div className="overflow-hidden rounded-xl border border-gray-200">
                    <DataTable
                        columns={columns}
                        data={rows}
                        pagination
                        paginationServer
                        paginationTotalRows={data?.count ?? 0}
                        paginationDefaultPage={page}
                        // Keeps the footer in step when a filter change resets the
                        // page programmatically (see `paginationResetToggle`).
                        paginationResetDefaultPage={paginationResetToggle}
                        onChangePage={(newPage: number) => setPage(newPage)}
                        onChangeRowsPerPage={(newPerPage: number, newPage: number) => {
                            setPageSize(newPerPage);
                            setPage(newPage);
                        }}
                        paginationPerPage={pageSize}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        progressPending={isLoading}
                        progressComponent={
                            <div className="py-20 text-center text-sm text-gray-500">
                                Loading disbursed loans...
                            </div>
                        }
                        noDataComponent={
                            <div className="py-20 text-center text-sm text-gray-500">
                                {filterError
                                    ? "Fix the date filter to see results."
                                    : hasFilters
                                      ? "No disbursed loans match the current filters."
                                      : "No disbursed loans yet."}
                            </div>
                        }
                        customStyles={customStyles}
                        highlightOnHover
                        dense
                        persistTableHead
                    />
                </div>
            </div>
        </div>
    );
};

export default DisbursedLoansTable;